import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { HeroSection } from './components/HeroSection';
import { BannerForm } from './components/BannerForm';
import { ResultSection } from './components/ResultSection';
import { HowItWorks } from './components/HowItWorks';
import { Footer } from '../../shared/components/Footer';
import { SEO } from '../../shared/components/SEO';
import { toast } from 'sonner';
import { generateBanner } from './utils/bannerGenerator';
import { trackToolUsed } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { AuthModal, AuthButton, CreditConfirmModal } from '../../components/auth';

const BannerGeneratorTool = () => {
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [lastDomain, setLastDomain] = useState(null);
  const [lastDirection, setLastDirection] = useState(null);

  const { isAuthenticated, credits, refreshUserProfile } = useAuth();

  const seoData = {
    title: 'Professional LinkedIn Banner Generator | Free AI-Powered Tool by Growleads.io',
    description: 'Paste your company domain and get an on-brand LinkedIn banner in seconds. AI reads your site and designs a banner tuned to your brand.',
    keywords: 'LinkedIn banner, LinkedIn cover image, profile banner generator, professional banner, AI banner generator, Growleads',
    canonicalUrl: 'https://tools.growleads.io/linkedin-banner-generator',
    ogImage: `${window.location.origin}/Banner trans.png`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Professional LinkedIn Banner Generator",
      "description": "Domain-only banner generator powered by AI — we read your company site and design a banner that feels on-brand.",
      "url": "https://tools.growleads.io/linkedin-banner-generator",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "creator": {
        "@type": "Organization",
        "name": "Growleads.io",
        "url": "https://growleads.io",
        "logo": "https://growleads.io/logo.png"
      },
      "featureList": [
        "Domain-only input",
        "AI-written tagline",
        "Brand-aligned palette",
        "LinkedIn-optimized dimensions"
      ],
      "screenshot": `${window.location.origin}/Banner trans.png`
    }
  };

  const handleGenerate = async (formData) => {
    if (!isAuthenticated) {
      setPendingData(formData);
      setAuthModalView('login');
      setShowAuthModal(true);
      return;
    }
    if (credits < 1) {
      toast.error('You have no credits left. Please upgrade to continue.');
      return;
    }
    setPendingData(formData);
    setShowCreditModal(true);
  };

  const executeGeneration = async (data) => {
    setIsLoading(true);
    setResult(null);

    try {
      const { companyDomain, divergeFrom } = data;
      const generatedResult = await generateBanner({ companyDomain, divergeFrom });
      setResult(generatedResult);
      setLastDomain(companyDomain);
      setLastDirection(generatedResult.directionTag || null);
      trackToolUsed('banner', { status: 'success', direction: generatedResult.directionTag });
      await refreshUserProfile();

      setTimeout(() => {
        document.querySelector('#results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error generating banner:', error);

      if (error.message === 'AUTH_REQUIRED') {
        setPendingData(data);
        setAuthModalView('login');
        setShowAuthModal(true);
        return;
      }
      if (error.message === 'INSUFFICIENT_CREDITS') {
        toast.error('You have no credits left. Please upgrade to continue.');
        return;
      }

      trackToolUsed('banner', { status: 'error', direction: data.divergeFrom });
      toast.error(error.message || 'We are experiencing high demand, try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreditConfirm = () => {
    setShowCreditModal(false);
    if (pendingData) {
      executeGeneration(pendingData);
      setPendingData(null);
    }
  };

  const handleReset = () => setResult(null);

  const handleRegenerate = () => {
    // Re-submit the same domain, telling the LLM to diverge from last direction.
    if (!lastDomain) {
      setResult(null);
      setTimeout(() => {
        document.querySelector('#generator')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    setResult(null);
    handleGenerate({ companyDomain: lastDomain, divergeFrom: lastDirection });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO {...seoData} />

      {/* Tool Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link
                to="/"
                className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden xs:inline">All Tools</span>
              </Link>
              <div className="h-6 w-px bg-border flex-shrink-0 hidden sm:block" />
              <a href="https://growleads.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90 transition-opacity min-w-0">
                <img
                  src="/Banner trans.png"
                  alt="Banner Generator"
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl object-contain flex-shrink-0"
                />
                <span className="font-display font-semibold text-foreground text-sm sm:text-lg truncate hidden sm:block">LinkedIn Banner Generator</span>
              </a>
            </div>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden md:flex items-center gap-6">
                <a href="#generator" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Generator</a>
                <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
              </div>
              <AuthButton />
            </nav>
          </div>
        </div>
      </header>

      <main>
        <HeroSection />
        <BannerForm onGenerate={handleGenerate} isLoading={isLoading} onReset={handleReset} hasResult={!!result} />
        <HowItWorks />
        {result && (
          <div id="results">
            <ResultSection result={result} onRegenerate={handleRegenerate} />
          </div>
        )}
      </main>

      <Footer
        logoSrc="/Banner trans.png"
        brandLabel="LinkedIn Banner Generator"
        ctaTitle="Want a Complete LinkedIn Profile Optimization?"
        ctaDescription="Use our LinkedIn Profile Optimizer tool to analyze and optimize your entire LinkedIn profile for better visibility and engagement."
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => { setShowAuthModal(false); setPendingData(null); }}
        defaultView={authModalView}
      />

      <CreditConfirmModal
        isOpen={showCreditModal}
        onClose={() => { setShowCreditModal(false); setPendingData(null); }}
        onConfirm={handleCreditConfirm}
        credits={1}
        toolName="Banner Generator"
        currentCredits={credits}
      />
    </div>
  );
};

export default BannerGeneratorTool;
